import { Component, ComponentType } from "@/types";
import { commandRegistry } from "./CommandRegistry";
import { textCommandHandler } from "./handlers/TextCommandHandler";
import { chartCommandHandler } from "./handlers/ChartCommandHandler";
import { mapCommandHandler } from "./handlers/MapCommandHandler";
import { buttonCommandHandler } from "./handlers/ButtonCommandHandler";
import { imageCommandHandler } from "./handlers/ImageCommandHandler";
import { iconCommandHandler } from "./handlers/IconCommandHandler";
import { inputCommandHandler } from "./handlers/InputCommandHandler";
import { containerCommandHandler } from "./handlers/ContainerCommandHandler";
import { flexContainerCommandHandler } from "./handlers/FlexContainerCommandHandler";
import { quickCommandHandler } from "./handlers/QuickCommandHandler";
import { formCommandHandler } from "./handlers/FormCommandHandler";
import { dataTextCommandHandler } from "./handlers/DataTextCommandHandler";
import { dataCardCommandHandler } from "./handlers/DataCardCommandHandler";
import { fileStorageCommandHandler } from "./handlers/FileStorageCommandHandler";
import { kanbanBoardCommandHandler } from "./handlers/KanbanBoardCommandHandler";
import { ganttChartCommandHandler } from "./handlers/GanttChartCommandHandler";
import { calendarCommandHandler } from "./handlers/CalendarCommandHandler";
import { questionnaireCommandHandler } from "./handlers/QuestionnaireCommandHandler";
import { votingCardCommandHandler } from "./handlers/VotingCardCommandHandler";
import { timeTrackerCommandHandler } from "./handlers/TimeTrackerCommandHandler";
import { checklistCommandHandler } from "./handlers/ChecklistCommandHandler";
import { radialDialCommandHandler } from "./handlers/RadialDialCommandHandler";
import { toggleSwitchCommandHandler } from "./handlers/ToggleSwitchCommandHandler";
import { statusLightCommandHandler } from "./handlers/StatusLightCommandHandler";
import { pushButtonCommandHandler } from "./handlers/PushButtonCommandHandler";
import { iconButtonCommandHandler } from "./handlers/IconButtonCommandHandler";
import { stepperCommandHandler } from "./handlers/StepperCommandHandler";
import { sliderCommandHandler } from "./handlers/SliderCommandHandler";
import { levelIndicatorCommandHandler } from "./handlers/LevelIndicatorCommandHandler";
import { logger } from "@/utils/logger";
import { segmentedControlCommandHandler } from "./handlers/SegmentedControlCommandHandler";
import { colorPickerCommandHandler } from "./handlers/ColorPickerCommandHandler";
import { thermostatCommandHandler } from "./handlers/ThermostatCommandHandler";
import { tableCommandHandler } from "./handlers/TableCommandHandler";
import { modalCommandHandler } from "./handlers/ModalCommandHandler";
import { timelineCommandHandler } from "./handlers/TimelineCommandHandler";
import { assignmentCommandHandler } from "./handlers/AssignmentCommandHandler";
import { automationCommandHandler } from "./handlers/AutomationCommandHandler";
import { CommandResult, PropertyChange } from "./BaseCommandHandler";

const BATCH_PROCESS_COMMAND_CONCURRENCY = 8;

/**
 * Main entry point for all component AI commands
 * Routes commands to appropriate handlers and manages the command processing pipeline
 */
export class CommandParser {
  private static instance: CommandParser;
  private initialized = false;

  private constructor() {
    this.initialize();
  }

  static getInstance(): CommandParser {
    if (!CommandParser.instance) {
      CommandParser.instance = new CommandParser();
    }
    return CommandParser.instance;
  }

  /**
   * Initialize and register all command handlers
   */
  private initialize(): void {
    if (this.initialized) return;

    // Register QuickCommandHandler with highest priority for all component types
    // This handler processes universal quick commands that work across all components
    const allComponentTypes = Object.values(ComponentType);

    // Register quick handler with highest priority (20)
    allComponentTypes.forEach(type => {
      commandRegistry.registerHandler(type, quickCommandHandler, 20);
    });

    // Register text handler
    commandRegistry.registerHandler(ComponentType.TEXT, textCommandHandler, 10);

    // Register data text handler
    commandRegistry.registerHandler(ComponentType.DATA_TEXT, dataTextCommandHandler, 10);

    // Register button handler
    commandRegistry.registerHandler(ComponentType.BUTTON, buttonCommandHandler, 10);

    // Register image handler
    commandRegistry.registerHandler(ComponentType.IMAGE, imageCommandHandler, 10);

    // Register icon handler
    commandRegistry.registerHandler(ComponentType.ICON, iconCommandHandler, 10);

    // Register input handler
    commandRegistry.registerHandler(ComponentType.INPUT, inputCommandHandler, 10);

    // Register container handler
    commandRegistry.registerHandler(ComponentType.CONTAINER, containerCommandHandler, 10);

    // Register flex-container handler with HIGHER priority than QuickCommandHandler (20)
    // This ensures Phase 1 patterns (brand colors, gradients, opacity) are processed correctly
    commandRegistry.registerHandler(ComponentType.FLEX_CONTAINER, flexContainerCommandHandler, 25);
    commandRegistry.registerHandler(ComponentType.CONTAINER, flexContainerCommandHandler, 25);

    // Register chart handlers for all chart types
    const chartTypes = [
      ComponentType.CHART,
      ComponentType.LINE_CHART,
      ComponentType.BAR_CHART,
      ComponentType.PIE_CHART,
      ComponentType.AREA_CHART,
      ComponentType.SCATTER_CHART,
      ComponentType.HEATMAP_CHART,
      ComponentType.TREEMAP_CHART,
      ComponentType.GAUGE_CHART,
      ComponentType.VERTICAL_GAUGE_CHART
    ];

    chartTypes.forEach(type => {
      commandRegistry.registerHandler(type, chartCommandHandler, 10);
    });

    // Register map handler for MAP_CHART type
    commandRegistry.registerHandler(ComponentType.MAP_CHART, mapCommandHandler, 10);
    logger.debug("Registered MapCommandHandler for MAP_CHART");

    // Register form handler for FORM_CONTAINER type
    commandRegistry.registerHandler(ComponentType.FORM_CONTAINER, formCommandHandler, 25);
    logger.debug("Registered FormCommandHandler for FORM_CONTAINER");

    // Register data card handler for DATA_CARD type
    commandRegistry.registerHandler(ComponentType.DATA_CARD, dataCardCommandHandler, 10);

    // Register file storage handler for FILE_STORAGE type
    commandRegistry.registerHandler(ComponentType.FILE_STORAGE, fileStorageCommandHandler, 10);

    // Register kanban board handler for KANBAN_BOARD type
    commandRegistry.registerHandler(ComponentType.KANBAN_BOARD, kanbanBoardCommandHandler, 10);

    // Register gantt chart handler for GANTT_CHART type
    commandRegistry.registerHandler(ComponentType.GANTT_CHART, ganttChartCommandHandler, 10);

    // Register calendar handler for CALENDAR type
    commandRegistry.registerHandler(ComponentType.CALENDAR, calendarCommandHandler, 10);

    // Register questionnaire handler for QUESTIONNAIRE type
    commandRegistry.registerHandler(ComponentType.QUESTIONNAIRE, questionnaireCommandHandler, 10);

    // Register voting card handler for VOTING_CARD type
    commandRegistry.registerHandler(ComponentType.VOTING_CARD, votingCardCommandHandler, 10);

    // Register time tracker handler for TIME_TRACKER type
    commandRegistry.registerHandler(ComponentType.TIME_TRACKER, timeTrackerCommandHandler, 10);

    // Register automation rules handler for PM components (priority 8 = checked before generic PM handlers)
    commandRegistry.registerHandler(ComponentType.KANBAN_BOARD, automationCommandHandler, 8);
    commandRegistry.registerHandler(ComponentType.GANTT_CHART, automationCommandHandler, 8);
    commandRegistry.registerHandler(ComponentType.CALENDAR, automationCommandHandler, 8);

    // Register checklist handler for CHECKLIST type
    commandRegistry.registerHandler(ComponentType.CHECKLIST, checklistCommandHandler, 10);

    // Register assignment widget handler for ASSIGNMENT_WIDGET type
    commandRegistry.registerHandler(ComponentType.ASSIGNMENT_WIDGET, assignmentCommandHandler, 10);

    // Register radial dial handler for RADIAL_DIAL type
    commandRegistry.registerHandler(ComponentType.RADIAL_DIAL, radialDialCommandHandler, 10);

    // Register toggle switch handler for TOGGLE_SWITCH type
    commandRegistry.registerHandler(ComponentType.TOGGLE_SWITCH, toggleSwitchCommandHandler, 10);

    // Register status light handler for STATUS_LIGHT type
    commandRegistry.registerHandler(ComponentType.STATUS_LIGHT, statusLightCommandHandler, 10);

    // Register push button handler for PUSH_BUTTON type
    commandRegistry.registerHandler(ComponentType.PUSH_BUTTON, pushButtonCommandHandler, 10);

    // Register icon button handler for ICON_BUTTON type
    commandRegistry.registerHandler(ComponentType.ICON_BUTTON, iconButtonCommandHandler, 10);

    // Register stepper handler for STEPPER type
    commandRegistry.registerHandler(ComponentType.STEPPER, stepperCommandHandler, 10);

    // Register slider handler for SLIDER type
    commandRegistry.registerHandler(ComponentType.SLIDER, sliderCommandHandler, 10);

    // Register level indicator handler for LEVEL_INDICATOR type
    commandRegistry.registerHandler(ComponentType.LEVEL_INDICATOR, levelIndicatorCommandHandler, 10);

    // Register segmented control handler for SEGMENTED_CONTROL type
    commandRegistry.registerHandler(ComponentType.SEGMENTED_CONTROL, segmentedControlCommandHandler, 10);

    // Register color picker handler for COLOR_PICKER type
    commandRegistry.registerHandler(ComponentType.COLOR_PICKER, colorPickerCommandHandler, 10);

    // Register thermostat handler for THERMOSTAT type
    commandRegistry.registerHandler(ComponentType.THERMOSTAT, thermostatCommandHandler, 10);

    // Register table handler for TABLE and ADVANCED_TABLE types
    const tableTypes = [ComponentType.TABLE, ComponentType.ADVANCED_TABLE];
    tableTypes.forEach(type => {
      commandRegistry.registerHandler(type, tableCommandHandler, 10);
    });

    // Register modal handler for MODAL type
    commandRegistry.registerHandler(ComponentType.MODAL, modalCommandHandler, 25);

    // Register timeline handler for HORIZONTAL_TIMELINE type
    commandRegistry.registerHandler(ComponentType.HORIZONTAL_TIMELINE, timelineCommandHandler, 10);

    this.initialized = true;
  }

  /**
   * Parse a command for a component
   */
  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    // Ensure handlers are registered
    this.initialize();

    // Clean and normalize command
    const normalizedCommand = this.normalizeCommand(command);

    // Process through registry
    const result = await commandRegistry.processCommand(normalizedCommand, component);

    // Add command history tracking (for future learning)
    this.trackCommand(normalizedCommand, component, result);

    return result;
  }

  /**
   * Parse multiple commands in sequence
   */
  async parseMultipleCommands(
    commands: string[],
    component: Component
  ): Promise<CommandResult[]> {
    const results: CommandResult[] = [];
    let currentComponent = { ...component };

    for (const command of commands) {
      const result = await this.parseCommand(command, currentComponent);
      results.push(result);

      // Apply changes to component for next command
      if (result.success && result.command?.changes) {
        currentComponent = this.applyChangesToComponent(
          currentComponent,
          result.command.changes
        );
      }
    }

    return results;
  }

  /**
   * Check if a command can be processed
   */
  canProcessCommand(command: string, component: Component): boolean {
    this.initialize();
    return commandRegistry.canProcessCommand(command, component);
  }

  /**
   * Get example commands for a component type
   */
  async getExampleCommands(componentType: ComponentType): Promise<string[]> {
    this.initialize();
    return commandRegistry.getExampleCommands(componentType);
  }

  /**
   * Get intelligent suggestions based on component and context
   */
  async getSuggestions(
    component: Component,
    context?: {
      recentCommands?: string[];
      userPreferences?: Record<string, unknown>;
    }
  ): Promise<string[]> {
    const handlers = commandRegistry.getHandlers(component.type);
    const suggestions: string[] = [];

    // Get suggestions from handlers
    for (const handler of handlers) {
      const handlerSuggestions = await handler.getSuggestions(component);
      suggestions.push(...handlerSuggestions);
    }

    // Add context-aware suggestions
    if (context?.recentCommands && context.recentCommands.length > 0) {
      // Suggest variations of recent commands
      const recentCommand = context.recentCommands[0];
      if (recentCommand.includes('bigger')) {
        suggestions.push('Make even bigger', 'Make smaller');
      }
      if (recentCommand.includes('color')) {
        suggestions.push('Try a different color', 'Add background color');
      }
    }

    // Remove duplicates and limit
    return [...new Set(suggestions)].slice(0, 10);
  }

  /**
   * Batch process commands for multiple components
   */
  async batchProcessCommands(
    command: string,
    components: Component[]
  ): Promise<Map<string, CommandResult>> {
    const results = new Map<string, CommandResult>();

    for (let index = 0; index < components.length; index += BATCH_PROCESS_COMMAND_CONCURRENCY) {
      const batch = components.slice(index, index + BATCH_PROCESS_COMMAND_CONCURRENCY);
      const batchResults = await Promise.all(
        batch.map(async (component) => {
          const result = await this.parseCommand(command, component);
          return { componentId: component.id, result };
        })
      );

      for (const batchResult of batchResults) {
        results.set(batchResult.componentId, batchResult.result);
      }
    }

    return results;
  }

  /**
   * Normalize command for consistent processing
   */
  private normalizeCommand(command: string): string {
    return command
      .trim()
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/[""]/g, '"') // Normalize quotes
      .replace(/['']/g, "'");
  }

  /**
   * Apply changes to a component (for preview/simulation)
   */
  private applyChangesToComponent(
    component: Component,
    changes: PropertyChange
  ): Component {
    const updated = { ...component };

    // Handle DATA_TEXT → TEXT conversion (disconnect data binding)
    if (changes.convertToText && component.type === ComponentType.DATA_TEXT) {
      const props = component.properties || {};
      // Extract current value to use as content, remove data-specific properties
      const dataTextKeys = new Set([
        'value',
        'dataSource',
        'showLabel',
        'emptyValueText',
        'valueField',
        'labelField',
        'label',
      ]);
      const otherProps = Object.fromEntries(
        Object.entries(props).filter(([key]) => !dataTextKeys.has(key))
      );

      updated.type = ComponentType.TEXT;
      updated.properties = {
        ...otherProps,
        content: props.value ?? '',
      };

      // Still apply any additional property changes
      if (changes.properties) {
        updated.properties = {
          ...updated.properties,
          ...changes.properties
        };
      }
      if (changes.style) {
        updated.style = {
          ...updated.style,
          ...changes.style
        };
      }

      return updated;
    }

    if (changes.type) {
      updated.type = changes.type;
    }

    if (changes.properties) {
      updated.properties = {
        ...updated.properties,
        ...changes.properties
      };
    }

    if (changes.style) {
      updated.style = {
        ...updated.style,
        ...changes.style
      };
    }

    if (changes.size) {
      updated.size = {
        width: this.toNumericDimension(
          changes.size.width,
          updated.size.width,
        ),
        height: this.toNumericDimension(
          changes.size.height,
          updated.size.height,
        ),
      };
    }

    if (changes.position) {
      const currentPosition = updated.position ?? { x: 0, y: 0, z: 0 };
      updated.position = {
        x: changes.position.x ?? currentPosition.x,
        y: changes.position.y ?? currentPosition.y,
        z: changes.position.z ?? currentPosition.z,
      };
    }

    return updated;
  }

  private toNumericDimension(
    value: number | string | undefined,
    fallback: number | string,
  ): number {
    if (typeof value === "number") {
      return value;
    }

    const normalizedFallback = typeof fallback === "number"
      ? fallback
      : Number.parseFloat(fallback) || 0;

    if (typeof value === "string") {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : normalizedFallback;
    }

    return normalizedFallback;
  }

  /**
   * Track command for learning and analytics
   */
  private trackCommand(
    command: string,
    component: Component,
    result: CommandResult
  ): void {
    // This could be extended to send telemetry or store in local storage
    // for improving suggestions and command recognition
    if (result.success) {
      console.debug('Command executed successfully:', {
        command,
        componentType: component.type,
        confidence: result.command?.confidence
      });
    }
  }

  /**
   * Get command history for learning
   */
  getCommandHistory(): Array<{
    command: string;
    componentType: ComponentType;
    success: boolean;
    timestamp: Date;
  }> {
    // This would retrieve from storage in a real implementation
    return [];
  }

  /**
   * Clear command history
   */
  clearCommandHistory(): void {
    // Clear stored history
  }
}

export const commandParser = CommandParser.getInstance();
